import React, {useEffect, useState, useRef} from 'react';
import {push} from "connected-react-router";
import {useSelector, useDispatch} from 'react-redux';

import { loadModules } from 'esri-loader';
import { esriOptions, axiosBaseURL } from '../../../../config/config';
import useDataSource from "../../../../hooks/useDataSource";
import InputIcon from "@mui/icons-material/Input";
import LockIcon from '@mui/icons-material/Lock';
import { styled, useTheme, LinearProgress, Grid, Paper, Button, Typography } from '@mui/material';


const PREFIX = 'EsriAuthWrapper';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    paper: `${PREFIX}-paper`,
    signInButton: `${PREFIX}-signInButton`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        flexGrow: 1,
        position: 'relative',
        width: '100%',
        height: 'calc(100% - 1px)',
        overflow: 'hidden',
        fontFamily: 'Roboto',
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.paper}`]: {
        width: '360px',
        height: '220px',
    },

    [`& .${classes.signInButton}`]: {
        marginTop: '16px'
    }
}));

export default React.memo(function EsriAuthWrapper({children, appId, portalUrl}) {


    // Component State
    const [authorizing, setAuthorizing] = useState(true);
    const [authorized, setAuthorized] = useState(false);
    const [esriIMError, setEsriIMError] = useState(null);

    useEffect(() => {

        signIn(false);

        return function cleanup() {
        };

    }, []);

    function signIn(autoSignIn) {

        loadModules(
            [
                'esri/identity/OAuthInfo',
                'esri/identity/IdentityManager'
            ],
            JSON.parse(esriOptions())
        ).then(
            ([
                 OAuthInfo,
                 esriId,
             ]) => {

                // Rehydrate persisted credentials
                let hydrated = false;
                var credentials = localStorage.getItem("msal.esri.credentials");
                if (credentials) {
                    const infoProps = JSON.parse(credentials);
                    esriId.initialize(infoProps);

                    // Check sign in status
                    esriId
                        .checkSignInStatus(infoProps.portalUrl + '/sharing')
                        .then(function () {
                            try {
                                const idConfig = JSON.stringify(esriId.toJSON());
                                localStorage.setItem("msal.esri.credentials", idConfig);
                            } catch (e) { console.log('LS **ERROR**', e); }

                            setAuthorized(true);
                            setAuthorizing(false);

                            hydrated = true;
                        })
                        .catch(function (error) {
                            // Not Hydrated but no reason to worry
                            console.log('EIMB **ERROR**', error);
                        });
                }

                if (!hydrated) {

                    let infoProps = {
                        // Swap this ID out with registered application ID
                        appId: appId,
                        // Uncomment the next line to prevent the user's signed in state from being shared with other apps on the same domain with the same authNamespace value.
                        // authNamespace: "portal_oauth_inline",
                        popup: true,
                        popupCallbackUrl: "{0}/oauth-callback.html".format(window.location.origin + axiosBaseURL()),
                        portalUrl: portalUrl
                    };

                    var info = new OAuthInfo(infoProps);
                    esriId.registerOAuthInfos([info]);

                    // Check sign in status
                    esriId
                        .checkSignInStatus(portalUrl + '/sharing')
                        .then(function () {
                            try {
                                const idConfig = JSON.stringify(esriId.toJSON());
                                localStorage.setItem("msal.esri.credentials", idConfig);
                            } catch (e) { console.log('LS **ERROR**', e); }

                            setAuthorized(true);
                            setAuthorizing(false);
                        })
                        .catch(function (error) {
                            if (autoSignIn) {
                                esriId.getCredential(portalUrl + "/sharing")
                                    .then(function (a) {
                                        try {
                                            const idConfig = JSON.stringify(esriId.toJSON());
                                            localStorage.setItem("msal.esri.credentials", idConfig);
                                        } catch (e) { console.log('LS **ERROR**', e); }

                                        setAuthorized(true);
                                        setAuthorizing(false);
                                    })
                                    .catch(function (error) {
                                        console.log('EIMC **ERROR**', error);
                                        setEsriIMError(error);
                                    });
                            } else {
                                setAuthorized(false);
                                setAuthorizing(false);
                            }
                        });
                }
            });
    }

    return (
        <Root className={classes.root}>
            {authorizing && !esriIMError &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {!authorizing && authorized && !esriIMError &&
                <React.Fragment>
                {children}
                </React.Fragment>
            }
            {!authorizing && !authorized && !esriIMError &&
            <Grid
                container
                spacing={0}
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ minHeight: 'calc(100% - 64px)' }}
                >
                    <Grid item xs={12}>
                    <div className={classes.paper}>
                    <div
                    style={{
                        height: '60px',
                        width: '100%',
                        padding: '0px 0px 16px 0px',
                        textAlign: 'center'
                    }}>
                        <LockIcon color="primary" style={{ fontSize: '44px' }} />
                    </div>
                    <div
                        style={{
                            width: '100%',
                            textAlign: 'center',
                            padding: '8px'
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                        Oh Snap!
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                        Looks like you're not signed in or have not authorized the use of your GIS Content.
                        </Typography>
                        <Button className={classes.signInButton} variant="contained" color="primary" onClick={() => { signIn(true) }}>Sign In</Button>
                    </div>
                    </div>
                </Grid>
            </Grid>
            }
            {esriIMError &&
            <Grid
                container
                spacing={0}
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ minHeight: 'calc(100% - 64px)' }}
            >
                <Grid item xs={12}>
                    <div className={classes.paper}>
                        <div
                            style={{
                                height: '60px',
                                width: 'calc(100% - 32px)',
                                padding: '16px',
                                textAlign: 'center',
                            }}
                        >
                            <InputIcon color="primary" style={{ fontSize: '64px' }} />
                        </div>
                        <div
                            style={{
                                width: 'calc(100% - 16px)',
                                textAlign: 'center',
                                padding: '8px',
                            }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Oh Snap!
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                Looks like you don't know the secret handshake :(
                            </Typography>
                        </div>
                    </div>
                </Grid>
            </Grid>
            }
        </Root>
    );
});
